import { useEffect, useState } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    current_workflow_challenge: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    // Load Calendly widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
      if (existingScript) {
        document.body.removeChild(existingScript);
      }
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Insert into Supabase leads table
      const { error } = await supabase
        .from('leads')
        .insert([
          {
            name: formData.name,
            email: formData.email,
            organization: formData.organization,
            message: formData.current_workflow_challenge,
            source: 'website',
            status: 'new'
          }
        ]);

      if (error) {
        console.error('Error submitting form:', error);
        alert('There was an error submitting your form. Please try again.');
        return;
      }

      // Send emails via Edge Function
      try {
        const emailResponse = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-workflow-audit-emails`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            organization: formData.organization,
            current_workflow_challenge: formData.current_workflow_challenge
          }),
        });

        if (!emailResponse.ok) {
          console.error('Email sending failed, but form was submitted successfully');
        }
      } catch (emailError) {
        console.error('Error sending emails:', emailError);
        // Don't show error to user since the form submission was successful
      }

      // Show success message
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        organization: '',
        current_workflow_challenge: ''
      });

    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20" style={{ backgroundColor: '#F8F4EF' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="bg-white rounded-3xl p-8 lg:p-16 shadow-sm border" style={{ borderColor: '#E6DACD' }}>
          <div className="text-center max-w-4xl mx-auto">
            <h2 
              className="text-4xl lg:text-5xl font-bold mb-6"
              style={{ color: '#2B2B2B', fontFamily: 'Manrope, Inter, sans-serif' }}
            >
              Schedule a Workflow Audit
            </h2>
            
            <p 
              className="text-xl mb-8 leading-relaxed"
              style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
            >
              15-minute review to identify bottlenecks, friction points, and automation opportunities.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="text-center">
                <div 
                  className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: '#E6DACD' }}
                >
                  <i 
                    className="ri-search-line text-2xl"
                    style={{ color: '#7B6655' }}
                  ></i>
                </div>
                <h3 
                  className="font-semibold mb-2"
                  style={{ color: '#2B2B2B', fontFamily: 'Manrope, Inter, sans-serif' }}
                >
                  Process Analysis
                </h3>
                <p 
                  className="text-sm"
                  style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                >
                  We identify manual bottlenecks
                </p>
              </div>
              
              <div className="text-center">
                <div 
                  className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: '#E6DACD' }}
                >
                  <i 
                    className="ri-lightbulb-line text-2xl"
                    style={{ color: '#7B6655' }}
                  ></i>
                </div>
                <h3 
                  className="font-semibold mb-2"
                  style={{ color: '#2B2B2B', fontFamily: 'Manrope, Inter, sans-serif' }}
                >
                  Custom Recommendations
                </h3>
                <p 
                  className="text-sm"
                  style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                >
                  Tailored automation solutions
                </p>
              </div>
              
              <div className="text-center">
                <div 
                  className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: '#E6DACD' }}
                >
                  <i 
                    className="ri-calendar-check-line text-2xl"
                    style={{ color: '#7B6655' }}
                  ></i>
                </div>
                <h3 
                  className="font-semibold mb-2"
                  style={{ color: '#2B2B2B', fontFamily: 'Manrope, Inter, sans-serif' }}
                >
                  No Commitment
                </h3>
                <p 
                  className="text-sm"
                  style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                >
                  Free consultation, no pressure
                </p>
              </div>
            </div>

            {/* Custom Form */}
            <div className="max-w-2xl mx-auto mb-12">
              {isSubmitted ? (
                <div 
                  className="bg-green-50 border border-green-200 rounded-xl p-8 mb-8"
                >
                  <div className="flex items-center justify-center mb-4">
                    <i 
                      className="ri-check-circle-fill text-3xl text-green-600"
                    ></i>
                  </div>
                  <h3 
                    className="text-xl font-semibold mb-2 text-green-800"
                    style={{ fontFamily: 'Manrope, Inter, sans-serif' }}
                  >
                    Thank you – we'll contact you shortly
                  </h3>
                  <p 
                    className="text-green-700"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    Your workflow audit request has been submitted successfully. We'll reach out within 24 hours to schedule your consultation.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6 mb-8">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label 
                        htmlFor="name"
                        className="block text-sm font-medium mb-2 text-left"
                        style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                      >
                        Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-colors"
                        style={{ 
                          borderColor: '#E6DACD',
                          focusRingColor: '#7B6655',
                          fontFamily: 'Inter, sans-serif'
                        }}
                        placeholder="Your full name"
                      />
                    </div>
                    
                    <div>
                      <label 
                        htmlFor="email"
                        className="block text-sm font-medium mb-2 text-left"
                        style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                      >
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-colors"
                        style={{ 
                          borderColor: '#E6DACD',
                          focusRingColor: '#7B6655',
                          fontFamily: 'Inter, sans-serif'
                        }}
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label 
                      htmlFor="organization"
                      className="block text-sm font-medium mb-2 text-left"
                      style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                    >
                      Organization
                    </label>
                    <input
                      type="text"
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-colors"
                      style={{ 
                        borderColor: '#E6DACD',
                        focusRingColor: '#7B6655',
                        fontFamily: 'Inter, sans-serif'
                      }}
                      placeholder="Your company or organization"
                    />
                  </div>
                  
                  <div>
                    <label 
                      htmlFor="current_workflow_challenge"
                      className="block text-sm font-medium mb-2 text-left"
                      style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                    >
                      Current Workflow Challenge
                    </label>
                    <textarea
                      id="current_workflow_challenge"
                      name="current_workflow_challenge"
                      value={formData.current_workflow_challenge}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-colors resize-none"
                      style={{ 
                        borderColor: '#E6DACD',
                        focusRingColor: '#7B6655',
                        fontFamily: 'Inter, sans-serif'
                      }}
                      placeholder="Describe your biggest workflow bottleneck or manual process that's slowing your team down..."
                      maxLength={500}
                    />
                    <div className="text-right mt-1">
                      <span 
                        className="text-xs"
                        style={{ color: '#7B6655', fontFamily: 'Inter, sans-serif' }}
                      >
                        {formData.current_workflow_challenge.length}/500
                      </span>
                    </div>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 px-8 rounded-xl font-semibold text-white transition-all duration-200 hover:opacity-90 disabled:opacity-50 whitespace-nowrap cursor-pointer"
                    style={{ 
                      backgroundColor: '#7B6655',
                      fontFamily: 'Manrope, Inter, sans-serif'
                    }}
                  >
                    {isSubmitting ? 'Submitting...' : 'Request Workflow Audit'}
                  </button>
                </form>
              )}
            </div>
            
            {/* Calendly Inline Widget */}
            <div className="max-w-2xl mx-auto">
              <h3 
                className="text-2xl font-semibold mb-6"
                style={{ color: '#2B2B2B', fontFamily: 'Manrope, Inter, sans-serif' }}
              >
                Or Schedule Directly
              </h3>
              <div 
                className="calendly-inline-widget" 
                data-url="https://calendly.com/moulderdj/workflow-audit-call"
                style={{ minWidth: '320px', height: '700px' }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
